import type { PlannerResourceLink } from '@/types'

const toText = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
  }

  if (typeof value === 'number') {
    return String(value)
  }

  return null
}

export function normalizePlannerResourceLink(value: unknown): PlannerResourceLink {
  const record = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>
  const normalizedOrderType = (record.order_type as string | number | null | undefined)
    ?? (record.order_type_id as string | number | null | undefined)
    ?? null
  const normalizedOrderTypeKey = toText(record.order_type_key) ?? toText(record.order_type__key)
  const normalizedOrderTypeLabel = toText(record.order_type_label) ?? toText(record.order_type__label)

  return {
    id: (record.id as string | number | undefined) ?? '',
    service_instance: (record.service_instance as string | number | null | undefined)
      ?? (record.service_instance_id as string | number | null | undefined)
      ?? null,
    service_instance_name: toText(record.service_instance_name),
    service_instance_identifier: toText(record.service_instance_identifier),
    planner_model: (record.planner_model as string | number | null | undefined) ?? (record.planner_model_id as string | number | null | undefined) ?? null,
    planner_model_id: (record.planner_model_id as string | number | null | undefined) ?? (record.planner_model as string | number | null | undefined) ?? null,
    planner_model_code: toText(record.planner_model_code) ?? toText(record.planner_model__code),
    planner_model_name: toText(record.planner_model_name) ?? toText(record.planner_model__name),
    planner_model_template_code: toText(record.planner_model_template_code) ?? toText(record.planner_model__template_code),
    planner_resource: (record.planner_resource as string | number | null | undefined) ?? (record.planner_resource_id as string | number | null | undefined) ?? null,
    planner_resource_id: (record.planner_resource_id as string | number | null | undefined) ?? (record.planner_resource as string | number | null | undefined) ?? null,
    planner_resource_role_name_id: (record.planner_resource_role_name_id as string | number | null | undefined) ?? (record.planner_resource__role_name_id as string | number | null | undefined) ?? null,
    planner_resource_role_name_unique_id: toText(record.planner_resource_role_name_unique_id) ?? toText(record.planner_resource__role_name__unique_id),
    planner_resource_role_name_title: toText(record.planner_resource_role_name_title) ?? toText(record.planner_resource__role_name__title),
    planner_resource_role_code: toText(record.planner_resource_role_code) ?? toText(record.planner_resource__role_code),
    planner_resource_label: toText(record.planner_resource_label) ?? toText(record.planner_resource__label),
    planner_resource_kind: toText(record.planner_resource_kind) ?? toText(record.planner_resource__resource_kind),
    planner_resource_min_count: typeof record.planner_resource_min_count === 'number' ? record.planner_resource_min_count : Number(record.planner_resource_min_count ?? '') || null,
    planner_resource_preferred_count: typeof record.planner_resource_preferred_count === 'number' ? record.planner_resource_preferred_count : record.planner_resource_preferred_count == null ? null : Number(record.planner_resource_preferred_count) || null,
    planner_resource_max_count: typeof record.planner_resource_max_count === 'number' ? record.planner_resource_max_count : Number(record.planner_resource_max_count ?? '') || null,
    product: (record.product as string | number | null | undefined) ?? null,
    product_name: toText(record.product_name),
    product_variant: (record.product_variant as string | number | null | undefined) ?? null,
    product_variant_label: toText(record.product_variant_label),
    product_variant_product_name: toText(record.product_variant_product_name),
    addon_item: (record.addon_item as string | number | null | undefined) ?? null,
    addon_item_name: toText(record.addon_item_name),
    addon_group_name: toText(record.addon_group_name),
    order: (record.order as string | number | null | undefined) ?? null,
    order_reference_id: toText(record.order_reference_id),
    order_type: normalizedOrderType,
    order_type_key: normalizedOrderTypeKey,
    order_type_label: normalizedOrderTypeLabel,
    serve_type: (record.serve_type as string | number | null | undefined)
      ?? (record.serve_type_id as string | number | null | undefined)
      ?? normalizedOrderType,
    serve_type_key: toText(record.serve_type_key) ?? toText(record.serve_type__key) ?? normalizedOrderTypeKey,
    serve_type_label: toText(record.serve_type_label) ?? toText(record.serve_type__label) ?? normalizedOrderTypeLabel,
    job_trigger_status: (record.job_trigger_status as string | number | null | undefined) ?? null,
    job_trigger_status_label: toText(record.job_trigger_status_label),
    job_creation_mode: toText(record.job_creation_mode),
    apply_to_all_orders: typeof record.apply_to_all_orders === 'boolean' ? record.apply_to_all_orders : record.apply_to_all_orders == null ? null : String(record.apply_to_all_orders).toLowerCase() === 'true',
    quantity: typeof record.quantity === 'number' ? record.quantity : Number(record.quantity ?? 0) || null,
    scope: toText(record.scope),
    is_required: typeof record.is_required === 'boolean' ? record.is_required : record.is_required == null ? null : String(record.is_required).toLowerCase() === 'true',
    notes: toText(record.notes),
    config_json: (record.config_json as Record<string, unknown> | null | undefined) ?? null,
    target_type: (record.target_type as PlannerResourceLink['target_type'] | undefined) ?? null,
    target_label: toText(record.target_label),
    effective_assignment_slots: typeof record.effective_assignment_slots === 'number' ? record.effective_assignment_slots : Number(record.effective_assignment_slots ?? '') || null,
    created_on: toText(record.created_on),
    updated_on: toText(record.updated_on),
  }
}

export function normalizePlannerResourceLinks(value: unknown): PlannerResourceLink[] {
  return Array.isArray(value)
    ? value.map((item) => normalizePlannerResourceLink(item)).filter((item) => String(item.id).trim() !== '')
    : []
}

export function mergePlannerResourceLinks(...collections: Array<PlannerResourceLink[] | undefined>): PlannerResourceLink[] {
  const map = new Map<string, PlannerResourceLink>()

  for (const collection of collections) {
    for (const link of collection ?? []) {
      const key = String(link.id)
      if (!key) {
        continue
      }
      map.set(key, link)
    }
  }

  return Array.from(map.values())
}

export function getPlannerLinkModelLabel(link: PlannerResourceLink): string {
  return link.planner_model_name || link.planner_model_code || 'Planner model'
}

export function getPlannerLinkResourceLabel(link: PlannerResourceLink): string {
  return link.planner_resource_role_name_title || link.planner_resource_label || link.planner_resource_role_code || 'Resource'
}